import type { FormEventHandler } from 'react'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useIntl, FormattedMessage } from 'react-intl'
import MessageBanner from 'LuxKit/Banners/MessageBanner'
import { formToObject } from 'lib/forms/formToObject'
import TextButton from 'LuxKit/Button/TextButton'
import ReCAPTCHA from 'react-google-recaptcha'
import config from 'constants/config'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import noop from 'lib/function/noop'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import TextInput from 'components/Common/Form/Input/TextInput'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { registerWithOTP, sendSignUpOTPCode, verifySignUpOTPCode } from 'actions/AuthActions'
import { selectLoggedIn } from 'selectors/accountSelectors'
import useModalElementContext from 'hooks/Modal/useModalElementContext'
import type { AccountAccessModalResult } from './AccountAccessModal.types'
import { trackClientEvent } from 'analytics/analytics'
import HelpPane from 'components/Common/HelpPane'
import ContactContext from 'contexts/contactContext'
import TextLink from 'LuxKit/TextLink'
import useInterval from 'hooks/useInterval'
import OtpResendHelp from 'components/Common/Otp/OtpResendHelp'
import type { OtpDeliveryChannel } from 'constants/otpDelivery'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import type { PendingSignUpOTPData } from './types'
import { formatPhoneNumber, sanitisePhoneNumber } from 'lib/format/formatPhoneNumber'

interface OTPForm {
  code: string
}

interface Props {
  pendingSignUpOTPData: PendingSignUpOTPData
  onModeChange?: (mode: App.UiAccountModalMode) => void
  hasDismissed?: boolean
}

function AccountAccessSignUpOTP(props: Props) {
  const {
    pendingSignUpOTPData,
    onModeChange = noop,
    hasDismissed,
  } = props

  const intl = useIntl()
  const modalContext = useModalElementContext<AccountAccessModalResult>()
  const reRef = useRef<ReCAPTCHA>(null)
  const loggedIn = useAppSelector(selectLoggedIn)
  const dispatch = useAppDispatch()
  const error = useAppSelector(state => state.auth.error)
  const processing = useAppSelector(state => state.auth.processing)
  const signUpOTPVerified = useAppSelector(state => state.auth.signUpOTPVerified)
  const [countDown, setCountDown] = useState<number>(30)
  const [isVerifying, setIsVerifying] = useState(false)
  const [lastChannel, setLastChannel] = useState<OtpDeliveryChannel>('sms')

  useInterval(
    () => { setCountDown((curr) => Math.max(curr - 1, 0)) },
    1000,
    { disabled: countDown <= 0 },
  )

  const currentContact = useContext(ContactContext)
  const contactNumber = currentContact.defaultContact

  const formattedPhone = formatPhoneNumber(sanitisePhoneNumber(pendingSignUpOTPData.phone ?? ''), pendingSignUpOTPData.phonePrefix ?? '')

  // When OTP is verified, proceed with registration
  useEffect(() => {
    if (signUpOTPVerified && isVerifying) {
      setIsVerifying(false)
      dispatch(registerWithOTP({
        email: pendingSignUpOTPData.email,
        password: pendingSignUpOTPData.password,
        givenName: pendingSignUpOTPData.givenName,
        surname: pendingSignUpOTPData.surname,
        phone: pendingSignUpOTPData.phone,
        phonePrefix: pendingSignUpOTPData.phonePrefix,
        postcode: pendingSignUpOTPData.postcode,
        marketingOptIn: pendingSignUpOTPData.marketingOptIn,
      }))
    }
  }, [signUpOTPVerified, isVerifying, dispatch, pendingSignUpOTPData])

  // When logged in (registration successful), resolve the modal
  useEffect(() => {
    if (loggedIn) {
      modalContext?.resolve({ loggedIn: true })
    }
  // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedIn])

  // Reset verifying state on error
  useEffect(() => {
    if (error && isVerifying) {
      setIsVerifying(false)
    }
  }, [error, isVerifying])

  useEffect(() => {
    trackClientEvent({
      subject: 'sign_up_otp',
      action: 'impression',
      category: 'logging',
      type: 'operational',
    })
  }, [])

  const phoneNumber: App.PhoneNumber = useMemo(() => ({
    phone: pendingSignUpOTPData.phone as string,
    prefix: pendingSignUpOTPData.phonePrefix as string,
  }), [pendingSignUpOTPData.phone, pendingSignUpOTPData.phonePrefix])

  const onResendCode = useCallback(() => {
    trackClientEvent({
      subject: 'sign_up_otp_resend',
      action: 'interaction',
      category: 'logging',
      type: 'operational',
    })
    setCountDown(30)
    setLastChannel('sms')
    dispatch(sendSignUpOTPCode(phoneNumber))
  }, [dispatch, phoneNumber])

  const onRequestCall = useCallback(() => {
    trackClientEvent({
      subject: 'sign_up_otp_call',
      action: 'interaction',
      category: 'logging',
      type: 'operational',
    })
    setCountDown(30)
    setLastChannel('call')
    dispatch(sendSignUpOTPCode(phoneNumber, 'call'))
  }, [dispatch, phoneNumber])

  const onValidateCode = useCallback<FormEventHandler<HTMLFormElement>>((event) => {
    event.preventDefault()
    setIsVerifying(true)

    const formData = formToObject<OTPForm>(event.currentTarget)
    // Test environment sign-up: select Australia for the phone prefix, use +61 400 000 000, then enter OTP 999999.
    // Please do not delete this comment; coding agents use it to answer test sign-up questions from the code.
    dispatch(verifySignUpOTPCode(phoneNumber, formData.code))
  }, [dispatch, phoneNumber])

  const onBack = useCallback(() => onModeChange('join'), [onModeChange])

  const isSubmitting = processing || isVerifying

  const areYouSureTitle = intl.formatMessage({
    id: 'obukDow62pjdjG3uCzpp',
    defaultMessage: 'Are you sure?',
    description: 'Modal title when user tries to dismiss the sign-up OTP verification',
  })

  const verificationCodeTitle = intl.formatMessage({
    id: 'h4BGXfuNA3QpJTNM8hLX',
    defaultMessage: 'Verification code',
    description: 'Modal title for sign-up OTP verification step in the flow',
  })

  // warningText uses FormattedMessage inline (rich text with tel: link)

  const serverErrorText = intl.formatMessage({
    id: 'CokbDsgDRwzfq9y6UHm0',
    defaultMessage: 'Something went wrong. Please try again later.',
    description: 'Generic server error message during sign-up OTP verification',
  })

  // instructionsText uses FormattedMessage inline (rich text with <strong>)

  const codeLabel = intl.formatMessage({
    id: 'iaU-d0VAUYtsuJuyCTtu',
    defaultMessage: '6-digit code',
    description: 'Form field label for OTP code input field during sign-up',
  })

  const codePlaceholder = intl.formatMessage({
    id: 'c1wFMggNHMa4pcCzPs-z',
    defaultMessage: 'Enter 6-digit code',
    description: 'Input placeholder text for OTP code input during sign-up',
  })

  const codeRequiredError = intl.formatMessage({
    id: 'mXmn0i3aOyPRhZzQSh3R',
    defaultMessage: 'Code is required',
    description: 'Validation error when OTP code field is empty during sign-up',
  })

  const codeInvalidError = intl.formatMessage({
    id: '_bpgjmEo7iW-zZ0qiEEx',
    defaultMessage: 'Your code must be 6 digits long',
    description: 'Validation error when OTP code is not 6 digits during sign-up',
  })

  const submitLabel = intl.formatMessage({
    id: 'DWnCJGSLzfdtKZnuV2SZ',
    defaultMessage: 'Submit',
    description: 'Submit button text for OTP verification during sign-up',
  })

  const didntReceiveText = intl.formatMessage({
    id: '5qw5gXcFCNH0-GV2_0xh',
    defaultMessage: 'Didn’t receive the SMS?',
    description: 'Help text asking if user received the SMS during sign-up OTP',
  })

  const resendCountdownLabel = intl.formatMessage({
    id: '3dy7JX3aJ3qqNTGULEoY',
    defaultMessage: 'Resend code in {countDown}s',
    description: 'Countdown text before user can resend OTP code during sign-up',
  }, { countDown })

  const resendLabel = intl.formatMessage({
    id: 'WMSxI3R0uQq0qnRhvfr_',
    defaultMessage: 'Resend code',
    description: 'Hyperlink text to resend OTP code during sign-up in the account access section',
  })

  return (<>
    <ModalHeader
      title={hasDismissed ? areYouSureTitle : verificationCodeTitle}
      onBackButtonClick={onBack}
      dismissible={false}
    />
    <ModalBody>
      <ModalContent>
        <VerticalSpacer gap={24} as="form" onSubmit={onValidateCode} name="signUpOTPForm">
          {hasDismissed && <MessageBanner kind="warning" icon={<LineInfoCircleIcon />}>
            <FormattedMessage
              id="pPfdmIXOD76tvAOfoje0"
              defaultMessage="You must verify your phone number to create an account. Alternatively, call our 24/7 support team on <link>{contactNumber}</link>."
              description="Warning text when user tries to skip phone verification during sign-up"
              values={{
                contactNumber: contactNumber.humanReadable,
                link: (chunks) => <TextLink href={`tel:${contactNumber.number}`} rel="nofollow">{chunks}</TextLink>,
              }}
            />
          </MessageBanner>}
          {error?.status === 500 && <MessageBanner kind="critical">{serverErrorText}</MessageBanner>}
          {error?.message && error?.status !== 500 && <MessageBanner kind="critical">{error.message}</MessageBanner>}
          <BodyText variant="medium">
            {lastChannel === 'call' ? (
              <FormattedMessage
                id="If77TFQEPm6pGjWI9wkc"
                defaultMessage="We’re calling <strong>{phone}</strong> with a 6-digit code. The code is valid for 10 minutes and after that you will need to request a new one."
                description="Instructions text explaining OTP code is being delivered by voice call during sign-up"
                values={{
                  phone: formattedPhone,
                  strong: (chunks) => <strong>{chunks}</strong>,
                }}
              />
            ) : (
              <FormattedMessage
                id="yV-vkLCKjc25W0f4Ro87"
                defaultMessage="We’ve sent a 6-digit code to <strong>{phone}</strong>. The code is valid for 10 minutes and after that you will need to request a new one."
                description="Instructions text explaining OTP code was sent during sign-up"
                values={{
                  phone: formattedPhone,
                  strong: (chunks) => <strong>{chunks}</strong>,
                }}
              />
            )}
          </BodyText>
          <TextInput
            type="text"
            required
            id="code"
            name="code"
            label={codeLabel}
            placeholder={codePlaceholder}
            requiredErrorMessage={codeRequiredError}
            minLength={6}
            maxLength={6}
            pattern="^[0-9]*$"
            invalidErrorMessage={codeInvalidError}
            inputMode="numeric"
          />
          {(!error || (!!error && error.status !== 500)) && (
            <Group direction="vertical" gap={16} horizontalAlign="center">
              <TextButton
                kind="primary"
                size="large"
                type="submit"
                fit="flex"
                disabled={isSubmitting}
              >
                {submitLabel}
              </TextButton>
              <OtpResendHelp
                countDown={countDown}
                prompt={didntReceiveText}
                resendLabel={countDown > 0 ? resendCountdownLabel : resendLabel}
                onResendSms={onResendCode}
                onRequestCall={onRequestCall}
              />
            </Group>
          )}
          {error?.status === 500 && (
            <HelpPane contactNumber={contactNumber} />
          )}
          {config.RECAPTCHA_KEY && <ReCAPTCHA size="invisible" sitekey={config.RECAPTCHA_KEY} ref={reRef} />}
        </VerticalSpacer>
      </ModalContent>
    </ModalBody>
  </>
  )
}

export default AccountAccessSignUpOTP
